import apiCall from ".";

async function getAllServices(params = {}) {
  try {
    return await apiCall("get", "marketplace/services", null, params);
  } catch (error) {
    throw new Error(`Failed to fetch marketplace services`);
  }
}

/**
 * Active pricing plans (PRO / PREMIUM / ULTIMATE), public. Same documents as
 * `getAllServices({ includePlans: true }).pricingPlans`, without the whole
 * service catalogue — used by the /shop home's plan cards.
 */
async function getPricingPlans() {
  try {
    return await apiCall("get", "marketplace/pricing-plans");
  } catch (error) {
    throw new Error(`Failed to fetch pricing plans`);
  }
}

async function getServiceById(id) {
  try {
    return await apiCall("get", `marketplace/services/${id}`);
  } catch (error) {
    throw new Error(`Failed to fetch service details.`);
  }
}

async function getServicesBySlugs(slugs) {
  try {
    const slugsParam = Array.isArray(slugs) ? slugs.join(",") : slugs;
    return await apiCall(
      "get",
      `marketplace/services/slugs?slugs=${slugsParam}`
    );
  } catch (error) {
    throw new Error(`Failed to fetch services by slugs.`);
  }
}

async function getServicesByCategory(category) {
  try {
    return await apiCall("get", `marketplace/services/category/${category}`);
  } catch (error) {
    throw new Error(`Failed to fetch services for category: ${category}`);
  }
}

async function getServiceBySlug(slug) {
  try {
    return await apiCall("get", `marketplace/services/slug/${slug}`);
  } catch (error) {
    throw new Error(`Failed to fetch service details.`);
  }
}

async function createService(data) {
  try {
    return await apiCall("post", "marketplace/services", data);
  } catch (error) {
    throw new Error(`Failed to create service.`);
  }
}

async function updateService(id, data) {
  try {
    return await apiCall("put", `marketplace/services/${id}`, data);
  } catch (error) {
    throw new Error(`Failed to update service details.`);
  }
}

async function deleteService(id) {
  try {
    return await apiCall("delete", `marketplace/services/${id}`);
  } catch (error) {
    throw new Error(`Failed to delete service.`);
  }
}

async function createOrder(orderData) {
  try {
    return await apiCall("post", "marketplace/orders", orderData);
  } catch (error) {
    // The axios interceptor returns error.response, so error.data is the response body
    const errorMessage =
      error?.data?.message || error?.message || "Failed to create order.";
    throw new Error(errorMessage);
  }
}

async function capturePayment(captureData) {
  try {
    return await apiCall("post", "marketplace/orders/capture", captureData);
  } catch (error) {
    throw new Error(`Failed to capture payment.`);
  }
}

async function getUserOrders(filters = {}) {
  try {
    return await apiCall("get", "marketplace/orders", null, filters);
  } catch (error) {
    throw new Error(`Failed to fetch orders.`);
  }
}

async function getOrderDetails(orderId) {
  try {
    return await apiCall("get", `marketplace/orders/${orderId}`);
  } catch (error) {
    throw new Error(`Failed to fetch order details.`);
  }
}

async function getUserEntitlements(gamePlayerId = null) {
  try {
    const params = gamePlayerId ? { gamePlayerId } : {};
    return await apiCall(
      "get",
      "marketplace/orders/entitlements",
      null,
      params
    );
  } catch (error) {
    throw new Error(`Failed to fetch user entitlements.`);
  }
}

async function recordConsumption(
  gamePlayerId,
  serviceSlug,
  quantity,
  description = "",
  clientRequestId = undefined
) {
  try {
    return await apiCall("post", "marketplace/orders/entitlements/consume", {
      gamePlayerId,
      serviceSlug,
      quantity,
      description,
      clientRequestId,
    });
  } catch (error) {
    // Do NOT collapse this into a generic sentence: a refusal here is a
    // POLICY's answer (e.g. "this service is delivered through a fulfillment
    // card, create one with Track existing work") and is the only thing telling
    // the operator what to do instead.
    const err = new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Failed to record consumption"
    );
    // Which rule refused, for logs/telemetry. `PolicyRegistry.enforce` stamps
    // this into the veto body; it is deliberately NOT shown to the operator.
    err.policy = error?.response?.data?.policy;
    throw err;
  }
}

// Subscription APIs
async function createSubscriptionOrder(orderData) {
  try {
    return await apiCall(
      "post",
      "marketplace/orders/subscription",
      orderData
    );
  } catch (error) {
    const errorMessage =
      error?.data?.message || error?.message || "Failed to create subscription order.";
    throw new Error(errorMessage);
  }
}

async function getActiveSubscription() {
  try {
    return await apiCall("get", "marketplace/orders/subscriptions/active");
  } catch (error) {
    throw new Error("Failed to fetch active subscription.");
  }
}

// Soft cancel: renewals stop, access lasts until the paid-through date. Errors pass through unchanged.
function cancelServiceSubscription(subscriptionId) {
  return apiCall(
    "post",
    `marketplace/orders/subscriptions/${subscriptionId}/cancel`
  );
}

async function cancelSubscription(subscriptionId) {
  try {
    return await cancelServiceSubscription(subscriptionId);
  } catch (error) {
    throw new Error("Failed to cancel subscription.");
  }
}

async function confirmSubscription(subscriptionId, paymentId) {
  try {
    return await apiCall(
      "post",
      `marketplace/orders/subscriptions/${subscriptionId}/confirm`,
      { paymentId }
    );
  } catch (error) {
    throw new Error("Failed to confirm subscription.");
  }
}

async function pauseSubscription(subscriptionId) {
  try {
    return await apiCall(
      "post",
      `marketplace/orders/subscriptions/${subscriptionId}/pause`
    );
  } catch (error) {
    throw new Error("Failed to pause subscription.");
  }
}

async function resumeSubscription(subscriptionId) {
  try {
    return await apiCall(
      "post",
      `marketplace/orders/subscriptions/${subscriptionId}/resume`
    );
  } catch (error) {
    throw new Error("Failed to resume subscription.");
  }
}

// Coupon APIs
/**
 * Coupon endpoints take the cart as coupon line items. Pass the ORDER lines
 * (`buildOrderItems` in cart/cartQuote.js: services + selected add-ons, the
 * same lines POST marketplace/orders gets) so the coupon is judged on what
 * will actually be charged. Raw cart items (with `_id`) are still accepted.
 */
const toCouponItems = (items = []) =>
  items.map((item) =>
    item.type
      ? {
          type: item.type,
          itemId: item.itemId,
          itemName: item.itemName,
          quantity: item.quantity || 1,
          pricePerUnit: item.pricePerUnit || 0,
          isUpgrade: item.isUpgrade || false,
        }
      : {
          type: item.isPlan ? "plan" : "service",
          // For plans: _id contains the slug (e.g., "pro", "premium", "ultimate")
          // For services: _id is the MongoDB ObjectId
          itemId: item._id,
          itemName: item.name || item.displayName,
          quantity: item.quantity || 1,
          pricePerUnit: item.priceInr || 0,
          isUpgrade: item.isUpgrade || false,
        }
  );

async function validateCoupon(couponCode, cartItems, cartTotal) {
  try {
    return await apiCall("post", "marketplace/coupons/validate", {
      couponCode: couponCode,
      cartItems: toCouponItems(cartItems),
      cartTotal,
    });
  } catch (error) {
    const errorMessage =
      error?.data?.message || error?.message || "Invalid coupon code";
    throw new Error(errorMessage);
  }
}

async function getAvailableCoupons(cartItems, cartTotal) {
  try {
    return await apiCall("post", "marketplace/coupons/available", {
      cartItems: toCouponItems(cartItems),
      cartTotal,
    });
  } catch (error) {
    throw new Error(`Failed to fetch available coupons`);
  }
}

/**
 * Server price of a cart: bundle discounts, coupon and totals
 * (POST marketplace/cart/quote; docs/marketplace/bundles.md). `items` are the
 * order lines; works signed out (no coupon then). `billingCountryCode` (ISO-2,
 * optional) picks the tax region server-side (catalogue v2 §3.3); omitted when
 * unknown.
 */
async function quoteCart(items, couponCode, billingCountryCode) {
  try {
    return await apiCall("post", "marketplace/cart/quote", {
      items,
      ...(couponCode ? { couponCode } : {}),
      ...(billingCountryCode ? { billingCountryCode } : {}),
    });
  } catch (error) {
    const err = new Error(
      error?.data?.message || error?.message || "Failed to price the cart"
    );
    err.status = error?.status;
    throw err;
  }
}

async function checkCouponApplicability(couponCode, cartItems, cartTotal) {
  try {
    return await apiCall("post", "marketplace/coupons/check", {
      code: couponCode,
      cartItems: cartItems.map((item) => ({
        type: item.isPlan ? "plan" : "service",
        itemId: item._id,
        itemName: item.name,
        quantity: item.quantity || 1,
        pricePerUnit: item.priceInr || 0,
      })),
      cartTotal,
    });
  } catch (error) {
    throw new Error(`Failed to check coupon applicability`);
  }
}

export {
  getAllServices,
  getPricingPlans,
  getServiceById,
  getServicesBySlugs,
  getServicesByCategory,
  getServiceBySlug,
  createService,
  updateService,
  deleteService,
  createOrder,
  capturePayment,
  getUserOrders,
  getOrderDetails,
  getUserEntitlements,
  recordConsumption,
  validateCoupon,
  getAvailableCoupons,
  quoteCart,
  checkCouponApplicability,
  createSubscriptionOrder,
  getActiveSubscription,
  cancelServiceSubscription,
  cancelSubscription,
  confirmSubscription,
  pauseSubscription,
  resumeSubscription,
};
